/**
 * Client-side structural validation for scan-target URLs. Catches
 * obviously malformed input (missing TLD, stray whitespace, non-http(s)
 * schemes) before it reaches the backend, instead of surfacing as a slow
 * "scan failed" after Playwright fails to resolve DNS.
 *
 * Mirrors the hostname rule in packages/backend/src/utils/helpers.ts's
 * normalizeUrl() — keep both in sync if this changes.
 */
export type ScanUrlValidation =
  | { ok: true; normalized: string }
  | { ok: false; error: string };

const SCHEME_PATTERN = /^([a-zA-Z][a-zA-Z0-9+\-]*):(?:\/\/)?/;

export function validateScanUrl(input: string): ScanUrlValidation {
  const trimmed = input.trim();
  if (!trimmed) {
    return { ok: false, error: 'Enter a website URL to scan.' };
  }
  if (/\s/.test(trimmed)) {
    return { ok: false, error: "URLs can't contain spaces." };
  }

  const schemeMatch = trimmed.match(SCHEME_PATTERN);
  let candidate: string;
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (scheme !== 'http' && scheme !== 'https') {
      return { ok: false, error: 'Only http:// and https:// URLs are supported.' };
    }
    candidate = trimmed;
  } else {
    candidate = `https://${trimmed}`;
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return { ok: false, error: "That doesn't look like a valid URL." };
  }

  const hostname = parsed.hostname.replace(/\.$/, '');
  const lastDot = hostname.lastIndexOf('.');
  const tld = lastDot === -1 ? '' : hostname.slice(lastDot + 1);
  if (!hostname.includes('.') || !/^[a-zA-Z]{2,}$/.test(tld)) {
    return {
      ok: false,
      error: "That doesn't look like a real domain. Try something like example.com.",
    };
  }

  return { ok: true, normalized: candidate };
}
